import { Injectable, NgZone, inject } from '@angular/core';
import type posthog from 'posthog-js';
import { environment } from '../../environments/environment';

/**
 * Thin PostHog wrapper. No-ops entirely until a project key is set in the
 * environment, so the app runs fine without analytics configured.
 *
 * posthog-js is loaded LAZILY (dynamic import) and only on live hosts — it
 * never weighs down the initial bundle. Events fired before the SDK lands
 * are queued and flushed once it's ready.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private _zone = inject(NgZone);
  private _ph?: typeof posthog;
  private _loading = false;
  private _queue: Array<() => void> = [];

  /** Only track on the real deployed domains — never localhost, IP, or preview. */
  private _isLiveHost(): boolean {
    const h = window.location.hostname;
    // Production hosts only. advocad.ai is matched EXACTLY (apex + www), not by
    // suffix — staging lives at staging.advocad.ai and carries the same PostHog
    // key, so a blanket `.advocad.ai` would pollute production analytics.
    return (
      h === 'advocad.app' ||
      h.endsWith('.advocad.app') ||
      h === 'advocad.ai' ||
      h === 'www.advocad.ai'
    );
  }

  /** Initialise PostHog once, on app start. No-ops without a key or off-live. */
  init(): void {
    const cfg = environment.posthog;
    if (this._loading || !cfg?.key || !this._isLiveHost()) return;
    this._loading = true;
    // Run outside Angular so PostHog's timers don't trigger change detection.
    this._zone.runOutsideAngular(() => {
      void import('posthog-js').then(({ default: ph }) => {
        ph.init(cfg.key, {
          api_host: cfg.host,
          defaults: '2026-05-30',
          person_profiles: 'identified_only',
        });
        this._ph = ph;
        this._queue.forEach((fn) => fn());
        this._queue = [];
      });
    });
  }

  /** Runs now if the SDK is up, else queues until it is (live hosts only). */
  private _when(fn: () => void): void {
    if (this._ph) fn();
    else if (this._loading) this._queue.push(fn);
  }

  /** Track an event (e.g. 'activity_created'). */
  capture(event: string, props?: Record<string, unknown>): void {
    this._when(() => this._ph!.capture(event, props));
  }

  /** Tie subsequent events to a known user (the Auth0 sub). */
  identify(id: string, props?: Record<string, unknown>): void {
    if (!id) return;
    this._when(() => this._ph!.identify(id, props));
  }

  /** Clear the identity on logout. */
  reset(): void {
    this._when(() => this._ph!.reset());
  }
}
